import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../_shared/GlassCard';
import { Youtube, TrendingDown, TrendingUp, Play } from 'lucide-react';

const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !hasAnimated.current) {
                hasAnimated.current = true;
                const start = performance.now();
                const animate = (now) => {
                    const progress = Math.min(1, (now - start) / duration);
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setCount(Math.floor(end * eased));
                    if (progress < 1) requestAnimationFrame(animate);
                };
                requestAnimationFrame(animate);
            }
        }, { threshold: 0.3 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const MonthCard = ({ month, dateRange, views, accent, trendIcon: TrendIcon, trendLabel }) => {
    const [animatedViews, ref] = useCounter(views);
    return (
        <GlassCard className={`min-h-[220px] ${accent.border}`}>
            <div ref={ref} className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-1">{dateRange}</p>
                        <h3 className={`text-3xl font-black ${accent.title}`}>{month}</h3>
                    </div>
                    <div className={`${accent.iconBg} p-2 rounded-xl`}>
                        <TrendIcon size={20} className={accent.iconText} />
                    </div>
                </div>
                <div className="mb-4">
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Views</p>
                    <p className="text-white text-4xl font-black">{animatedViews.toLocaleString()}</p>
                </div>
                <div className="mt-auto pt-3 border-t border-white/5">
                    <p className={`text-xs ${accent.tagText} font-semibold`}>{trendLabel}</p>
                </div>
            </div>
        </GlassCard>
    );
};

const VideoTile = ({ rank, title, date, views, url, gradient, emoji }) => (
    <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-3xl overflow-hidden transition-transform duration-300 hover:scale-[1.03]"
        style={{ aspectRatio: '9/16' }}
    >
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-5xl mb-3 opacity-30 group-hover:opacity-0 transition-opacity duration-300">{emoji}</span>
        </div>
        <div className="absolute top-1/3 left-3 right-3">
            <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
                <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{title}</p>
            </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 backdrop-blur-0 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                    <Play size={20} className="text-white ml-0.5" fill="white" />
                </div>
            </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
            <p className="text-white text-sm font-bold">{views} views</p>
            <p className="text-white/60 text-[10px] font-mono mt-0.5">{date}</p>
        </div>
        <div className="absolute top-2 left-2">
            <span className="bg-black/40 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-white/10">#{rank}</span>
        </div>
    </a>
);

const marchVideos = [
    { title: 'WIFI ROUTER FULL-BODY SURVEILLANCE', date: 'Mar 13', views: '29K', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', url: 'https://www.youtube.com/shorts/Tsfx3VY7in8', emoji: '📡' },
    { title: 'PEWDIEPIE BUILT HIS OWN AI CHATBOT', date: 'Mar 5', views: '24K', gradient: 'from-rose-600 via-red-500 to-pink-400', url: 'https://www.youtube.com/shorts/32UWjRNCpjE', emoji: '🎮' },
    { title: 'PENTAGON THREATENED ANTHROPIC', date: 'Mar 2', views: '22K', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://www.youtube.com/shorts/7RcTzfLwxMs', emoji: '⚔️' },
    { title: 'CLAUDE BREAKS INTO MEXICAN GOVT SYSTEMS', date: 'Mar 9', views: '18K', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', url: 'https://www.youtube.com/shorts/cjDslSg250I', emoji: '🔓' },
    { title: 'AI AGENTS TAKE OVER GOOGLE WORKSPACE', date: 'Mar 17', views: '14K', gradient: 'from-amber-600 via-yellow-500 to-orange-400', url: 'https://www.youtube.com/shorts/LLdXpZsPG6k', emoji: '🤖' },
];

const aprilVideos = [
    { title: '12 VISUAL PROMPTS YOU MUST COPY', date: 'Apr 24', views: '12K', gradient: 'from-cyan-600 via-sky-500 to-blue-400', url: '#', emoji: '🎨' },
    { title: 'BENGALURU ACCOUNTABILITY PLATFORM', date: 'Apr 16', views: '11K', gradient: 'from-violet-600 via-purple-500 to-fuchsia-400', url: '#', emoji: '🏛️' },
    { title: 'OPENAI RAISED MORE THAN MOST COUNTRIES', date: 'Apr 6', views: '8.3K', gradient: 'from-emerald-600 via-green-500 to-teal-400', url: '#', emoji: '💰' },
    { title: 'ELON OUTPLAYED SAM ALTMAN', date: 'Apr 3', views: '5.8K', gradient: 'from-rose-600 via-red-500 to-orange-400', url: '#', emoji: '⚡' },
    { title: 'AI THAT DOES YOUR ENTIRE DESKTOP JOB', date: 'Apr 9', views: '5K', gradient: 'from-yellow-600 via-amber-500 to-orange-400', url: '#', emoji: '🖥️' },
];

export const YouTubeBreakdown = () => {
    return (
        <section className="py-20 px-4">
            <div className="container mx-auto max-w-6xl">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-red-500/30 bg-red-500/10 backdrop-blur-md mb-3">
                        <Youtube size={14} className="text-red-300 mr-2" />
                        <span className="text-sm font-medium text-red-300 tracking-wide uppercase">YouTube · Primary Surface</span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-3">
                        YouTube Performance
                    </h2>
                    <p className="text-white/50 max-w-2xl mx-auto">
                        YouTube is now the more reliable platform — steadier baseline, larger absolute view total, and a freshly-crossed 10K subscriber milestone. Top videos averaged ~10–12K views in both months.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                    <MonthCard
                        month="March"
                        dateRange="Mar 1 – 31"
                        views={742000}
                        accent={{
                            border: 'border-emerald-500/20',
                            title: 'text-emerald-300',
                            iconBg: 'bg-emerald-500/20',
                            iconText: 'text-emerald-300',
                            tagText: 'text-emerald-400',
                        }}
                        trendIcon={TrendingUp}
                        trendLabel="Strongest month · led by WiFi & Pentagon hits"
                    />
                    <MonthCard
                        month="April"
                        dateRange="Apr 1 – 30"
                        views={628748}
                        accent={{
                            border: 'border-amber-500/20',
                            title: 'text-amber-300',
                            iconBg: 'bg-amber-500/20',
                            iconText: 'text-amber-300',
                            tagText: 'text-amber-400',
                        }}
                        trendIcon={TrendingDown}
                        trendLabel="Held steady · -15% vs March (much softer than IG)"
                    />
                    <MonthCard
                        month="Combined"
                        dateRange="Mar 1 – Apr 30"
                        views={1370748}
                        accent={{
                            border: 'border-red-500/20',
                            title: 'text-red-300',
                            iconBg: 'bg-red-500/20',
                            iconText: 'text-red-300',
                            tagText: 'text-red-300',
                        }}
                        trendIcon={Youtube}
                        trendLabel="+2,400 subscribers · 10K milestone crossed"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <h3 className="text-emerald-300 font-bold text-lg mb-4 uppercase tracking-wider">Top 5 · March</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                            {marchVideos.slice(0, 3).map((v, i) => <VideoTile key={i} rank={i + 1} {...v} />)}
                            {marchVideos.slice(3, 5).map((v, i) => <VideoTile key={i + 3} rank={i + 4} {...v} />)}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-amber-300 font-bold text-lg mb-4 uppercase tracking-wider">Top 5 · April</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                            {aprilVideos.slice(0, 3).map((v, i) => <VideoTile key={i} rank={i + 1} {...v} />)}
                            {aprilVideos.slice(3, 5).map((v, i) => <VideoTile key={i + 3} rank={i + 4} {...v} />)}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
